import type {
  CompetitionId,
  CompetitionView,
  CompoundMarketTemplateConfig,
  EventBasedMarketView,
  EventId,
  EventLevelCompoundMarketTemplateColumnClientConfig,
  EventLevelCompoundMarketTemplateId,
  EventLevelMarketTemplateId,
  EventTemplate,
  EventTypeView,
  EventView,
  MarketId,
  MarketLevelCompoundMarketTemplateColumnClientConfig,
  MarketLevelCompoundMarketTemplateId,
  MarketLevelCompoundMarketTemplatePlayerAlternativesConfig,
  MarketLevelCompoundMarketTemplateRowConfig,
  MarketLevelMarketTemplateId,
  MarketTemplate,
  MarketView,
  MeetingBasedEventView,
  MeetingBasedMarketView,
  MeetingId,
  MeetingView,
  RaceId,
  RaceView,
  SingleMarketTemplateConfig,
  TemplateView
} from '@ps/isp-web-services-types';

import type { DisplayConfig as EventTableDisplayConfig } from '../components/MarketTemplate/templates/EventTable/EventTable';
import type { DisplayConfig as RaceCardDisplayConfig } from '../components/MarketTemplate/templates/RaceCard/RaceCard';

import type { EventTypeId } from './eventType';

export interface MarketDisplayConfig {
  RACE_CARD: RaceCardDisplayConfig;
  EVENT_TABLE: EventTableDisplayConfig;
}

export type SingleMarketTemplateDisplayConfig = MarketDisplayConfig[Extract<
  MarketLevelMarketTemplateId | EventLevelMarketTemplateId,
  keyof MarketDisplayConfig
>];

export type CompoundMarketTemplateDisplayConfig = MarketDisplayConfig[Extract<
  MarketLevelCompoundMarketTemplateId | EventLevelCompoundMarketTemplateId,
  keyof MarketDisplayConfig
>];

export type MarketLevelTemplateId =
  | MarketLevelMarketTemplateId
  | MarketLevelCompoundMarketTemplateId;

export type EventLevelTemplateId = EventLevelMarketTemplateId | EventLevelCompoundMarketTemplateId;

export type MarketTemplateType = 'BASE' | 'EVENT' | 'MEETING';

export interface SingleMarketTemplateProps {
  marketId: MarketId;
  displayConfig?: SingleMarketTemplateDisplayConfig;
  templateConfig?: SingleMarketTemplateConfig;
}

export type MarketLevelCompoundMarketTableTemplateConfig =
  | MarketLevelCompoundMarketTemplateRowConfig
  | MarketLevelCompoundMarketTemplateColumnClientConfig
  | MarketLevelCompoundMarketTemplatePlayerAlternativesConfig;

export type EventLevelCompoundMarketTableTemplateConfig =
  EventLevelCompoundMarketTemplateColumnClientConfig;

export type CompoundMarketTemplateColumnClientConfig =
  | MarketLevelCompoundMarketTemplateColumnClientConfig
  | EventLevelCompoundMarketTemplateColumnClientConfig;

interface BaseCompoundMarketTemplateProps {
  marketIds: (MarketId | null)[];
  displayConfig?: CompoundMarketTemplateDisplayConfig;
}

export interface MarketLevelCompoundMarketTemplateProps extends BaseCompoundMarketTemplateProps {
  config: MarketLevelCompoundMarketTableTemplateConfig | CompoundMarketTemplateConfig;
}

export interface EventLevelCompoundMarketTemplateProps extends BaseCompoundMarketTemplateProps {
  config: EventLevelCompoundMarketTableTemplateConfig;
}

export type CompoundMarketTemplateProps =
  | MarketLevelCompoundMarketTemplateProps
  | EventLevelCompoundMarketTemplateProps;

export type MarketTemplateProps = SingleMarketTemplateProps | CompoundMarketTemplateProps;

export interface BasicMarketTemplatesView {
  eventTypes: Partial<Record<EventTypeId, EventTypeView>>;
  markets: Partial<Record<MarketId, MarketView>>;
}

export interface EventBasedMarketTemplatesView extends BasicMarketTemplatesView {
  events: Record<EventId, EventView>;
  competitions: Record<CompetitionId, CompetitionView>;
  markets: Partial<Record<MarketId, EventBasedMarketView>>;
}

export interface MeetingBasedMarketTemplatesView extends BasicMarketTemplatesView {
  markets: Partial<Record<MarketId, MeetingBasedMarketView>>;
  meetings: Record<MeetingId, MeetingView>;
  races: Record<RaceId, RaceView>;
}

export type MarketTemplatesView =
  | BasicMarketTemplatesView
  | EventBasedMarketTemplatesView
  | MeetingBasedMarketTemplatesView;

export type MarketTemplateComponent<P = object> = React.FC<P> & {
  marketTemplateType: MarketTemplateType;
};

export type MarketTemplateElementType<P = object> = React.ElementType<P> & {
  marketTemplateType: MarketTemplateType;
};

export type InPlayCompetitionGroup = InPlayMarketTypeGroup[];

export type InPlayMarketTypeGroup = TemplateView[];

export type EventLevelEventTemplateId = 'EVENT_MORE_MARKETS';

export interface AntePostBasedMarketTemplatesView extends BasicMarketTemplatesView {
  events: Record<EventId, MeetingBasedEventView>;
}

export interface MarketTemplateView {
  template: MarketTemplate;
}

export interface EventTemplateView {
  template: EventTemplate;
}

export const isEventTemplate = (template: TemplateView['template']): template is EventTemplate =>
  'eventId' in template;

export const isMarketTemplate = (template: TemplateView['template']): template is MarketTemplate =>
  'marketId' in template;

export const isMarketTemplateView = (
  templateView: TemplateView
): templateView is MarketTemplateView => isMarketTemplate(templateView.template);

export const isEventTemplateView = (
  templateView: TemplateView
): templateView is EventTemplateView => isEventTemplate(templateView.template);
